/**
 * Single frontend API boundary (APP-001 §10, §33 invariants 1–4).
 *
 * Every dashboard request flows through `apiRequest`. The client:
 *   - resolves the configured API base URL (validated at startup);
 *   - obtains the current Clerk session token at request time (never stored);
 *   - attaches `Authorization: Bearer <clerk-session-jwt>` to authenticated
 *     dashboard routes only;
 *   - sets JSON Content-Type where a body is present;
 *   - threads a client request ID through `X-Request-ID` only when supported;
 *   - parses the provider-neutral DIRECT-011 error envelope;
 *   - preserves the server request ID on every error;
 *   - supports request cancellation via AbortSignal;
 *   - distinguishes network errors from API errors;
 *   - never logs tokens or secret response fields.
 *
 * The browser is never authoritative for account, plan, quota, or billing
 * state — it reads them exclusively from backend responses.
 */
import { loadConfig } from '@/config/env';
import {
  abortedError,
  isApiError,
  isAuthError,
  isValidationError,
  networkError,
  parseApiError,
  type ApiError,
} from './errors';

/** Token provider abstraction. Clerk's `getToken()` is injected at runtime. */
export type SessionTokenProvider = () => Promise<string | null>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** When false, skip the Clerk bearer token (e.g. public checker). */
  authenticated?: boolean;
  /** Extra headers (merged; never override Authorization/Content-Type). */
  headers?: Record<string, string>;
  /** Idempotency key for safe mutations (e.g. checkout). Sent as X-Idempotency-Key. */
  idempotencyKey?: string;
}

export interface ApiResult<T> {
  data: T;
  response: Response;
  requestId?: string;
}

let tokenProvider: SessionTokenProvider | null = null;

/**
 * Install the Clerk session token provider. Called once at app bootstrap
 * from the Clerk <ClerkProvider>. The provider is a function so the token is
 * always fetched fresh (Clerk refreshes short-lived JWTs automatically) and is
 * never held in module state.
 */
export function configureSessionTokenProvider(provider: SessionTokenProvider): void {
  tokenProvider = provider;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const { apiBaseUrl } = loadConfig();
  const base = apiBaseUrl.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${suffix}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function maybeClientRequestId(): string | undefined {
  // Stable, collision-resistant client request ID for diagnostics. This is NOT
  // an idempotency key and carries no account information. The server accepts
  // well-formed values and regenerates malformed ones.
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `client_${crypto.randomUUID()}`;
    }
  } catch {
    /* fall through */
  }
  return undefined;
}

/**
 * Core request function. Throws `ApiError` on any failure; returns typed data
 * on success. Network failures and server errors are distinguishable via
 * `ApiError.isNetworkError` / `ApiError.status`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const {
    method = 'GET',
    body,
    query,
    signal,
    authenticated = true,
    headers = {},
    idempotencyKey,
  } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  const clientRequestId = maybeClientRequestId();
  if (clientRequestId) {
    finalHeaders['X-Request-ID'] = clientRequestId;
  }
  if (idempotencyKey) {
    finalHeaders['X-Idempotency-Key'] = idempotencyKey;
  }

  if (authenticated) {
    if (!tokenProvider) {
      throw networkError('Session token provider is not configured.');
    }
    const token = await tokenProvider();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
    // If no token is available, proceed without it; the backend will respond
    // with 401 MISSING_SESSION and the caller handles that. The client never
    // fabricates identity.
  }

  let serializedBody: string | undefined;
  if (body !== undefined) {
    serializedBody = JSON.stringify(body);
    finalHeaders['Content-Type'] = 'application/json';
    finalHeaders.Accept = 'application/json';
  } else if (!finalHeaders.Accept) {
    finalHeaders.Accept = 'application/json';
  }

  let response: Response;
  try {
    const url = buildUrl(path, query);
    try {
      response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: serializedBody,
        signal,
        // Dashboard responses are credentialed via Bearer token, not cookies.
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (syncErr) {
      // Cross-realm AbortSignal quirk: some test/jsdom environments reject an
      // AbortSignal whose constructor differs from the fetch implementation's
      // internal AbortSignal reference ("Expected signal to be an instance of
      // AbortSignal"). This never occurs in real browsers. When the caller did
      // not already abort, retry once without the signal so the request still
      // completes; if the caller *did* abort, surface the abort.
      if (
        signal &&
        !signal.aborted &&
        syncErr instanceof TypeError &&
        /AbortSignal/i.test(syncErr.message)
      ) {
        response = await fetch(url, {
          method,
          headers: finalHeaders,
          body: serializedBody,
          credentials: 'omit',
          mode: 'cors',
        });
      } else if (signal?.aborted) {
        // The signal was already aborted; the cross-realm TypeError is just
        // how jsdom surfaces the abort. Treat it as a real cancellation.
        throw abortedError();
      } else {
        throw syncErr;
      }
    }
  } catch (err) {
    // An ApiError thrown by the inner block (e.g. abortedError) must pass
    // through unchanged rather than being rewrapped as a network error.
    if (isApiError(err)) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw abortedError();
    }
    throw networkError();
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const requestId = response.headers.get('x-request-id') ?? clientRequestId;

  // 204 No Content / empty bodies.
  if (response.status === 204) {
    return { data: undefined as T, response, requestId };
  }
  const text = await response.text();
  if (!text) {
    return { data: undefined as T, response, requestId };
  }
  try {
    return { data: JSON.parse(text) as T, response, requestId };
  } catch {
    // Treat unparseable success bodies as a parse error (should not happen).
    const { parseError } = await import('./errors');
    throw parseError(response.status, requestId);
  }
}

/**
 * TanStack Query retry predicate (APP-001 §11). Bounded retries for transient
 * network/5xx; never retry authentication or validation errors.
 */
export function defaultRetry(failureCount: number, error: unknown): boolean {
  if (!isApiError(error)) return failureCount < 1;
  const err = error as ApiError;
  if (err.isAborted) return false;
  if (isAuthError(err) || isValidationError(err)) return false;
  if (err.status === 403 || err.status === 404 || err.status === 409) return false;
  if (err.isNetworkError || err.status >= 500) return failureCount < 3;
  return err.retryable ? failureCount < 3 : false;
}

export type { ApiError };

// ---------------------------------------------------------------------------
// APP-007 — multipart upload + blob download helpers.
//
// `apiRequest` forces JSON.stringify + Content-Type: application/json for any
// body, and parses every success body as JSON. Bulk upload needs raw
// multipart/form-data (the browser must set the boundary) and bulk result
// download needs a binary Blob (CSV). These two siblings reuse the SAME
// token/X-Request-ID/credentials/AbortSignal/error-parsing plumbing as
// `apiRequest` so auth, error envelopes, and request IDs stay consistent.
//
// Neither helper logs the request body, file content, or result bytes.
// ------------------------------------------------------------------------- */

/** Options shared by the upload + blob helpers (mirrors RequestOptions' relevant fields). */
export interface ApiFileOptions {
  signal?: AbortSignal;
  /** When false, skip the Clerk bearer token. Default true. */
  authenticated?: boolean;
  /** Extra headers (merged; never override Authorization/Content-Type). */
  headers?: Record<string, string>;
  /** Optional idempotency key sent as X-Idempotency-Key. */
  idempotencyKey?: string;
}

/** Result of a multipart upload (JSON DTO returned by the server). */
export interface ApiUploadResult<T> {
  data: T;
  response: Response;
  requestId?: string;
}

/**
 * Submit a file as `multipart/form-data`. The browser constructs the multipart
 * boundary; the caller MUST NOT set Content-Type (doing so would strip the
 * boundary). Only the single `file` field is appended (the bulk contract
 * accepts no other form fields). Auth, X-Request-ID, signal, and DIRECT-011
 * error parsing are identical to `apiRequest`. Never logs file content.
 */
export async function apiUpload<T>(
  path: string,
  file: Blob,
  fieldName: string,
  options: ApiFileOptions = {},
): Promise<ApiUploadResult<T>> {
  const { signal, authenticated = true, headers = {}, idempotencyKey } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  const clientRequestId = maybeClientRequestId();
  if (clientRequestId) {
    finalHeaders['X-Request-ID'] = clientRequestId;
  }
  if (idempotencyKey) {
    finalHeaders['X-Idempotency-Key'] = idempotencyKey;
  }
  if (authenticated) {
    if (!tokenProvider) {
      throw networkError('Session token provider is not configured.');
    }
    const token = await tokenProvider();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }
  // Deliberately do NOT set Content-Type: the browser must set the multipart
  // boundary. Setting it here would corrupt the body.
  finalHeaders.Accept = 'application/json';

  const formData = new FormData();
  formData.append(fieldName, file);

  let response: Response;
  try {
    const url = buildUrl(path);
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: finalHeaders,
        body: formData,
        signal,
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (syncErr) {
      // Cross-realm AbortSignal quirk — see apiRequest for the rationale.
      if (
        signal &&
        !signal.aborted &&
        syncErr instanceof TypeError &&
        /AbortSignal/i.test(syncErr.message)
      ) {
        response = await fetch(url, {
          method: 'POST',
          headers: finalHeaders,
          body: formData,
          credentials: 'omit',
          mode: 'cors',
        });
      } else if (signal?.aborted) {
        throw abortedError();
      } else {
        throw syncErr;
      }
    }
  } catch (err) {
    if (isApiError(err)) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw abortedError();
    }
    throw networkError();
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const requestId = response.headers.get('x-request-id') ?? clientRequestId;
  if (response.status === 204) {
    return { data: undefined as T, response, requestId };
  }
  const text = await response.text();
  if (!text) {
    return { data: undefined as T, response, requestId };
  }
  try {
    return { data: JSON.parse(text) as T, response, requestId };
  } catch {
    const { parseError } = await import('./errors');
    throw parseError(response.status, requestId);
  }
}

/** Result of a blob download (binary body + safe parsed filename). */
export interface ApiBlobResult {
  blob: Blob;
  response: Response;
  requestId?: string;
  /** Filename parsed safely from Content-Disposition, when present. */
  filename?: string;
}

/**
 * Download a binary resource (e.g. the bulk result CSV) through the
 * authenticated client. Returns the response as a Blob plus a safely parsed
 * filename (from Content-Disposition). The caller is responsible for creating
 * an object URL, triggering the download, and revoking the URL promptly; this
 * helper never persists the blob or URL. Never logs result bytes.
 */
export async function apiBlob(path: string, options: ApiFileOptions = {}): Promise<ApiBlobResult> {
  const { signal, authenticated = true, headers = {}, idempotencyKey } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  const clientRequestId = maybeClientRequestId();
  if (clientRequestId) {
    finalHeaders['X-Request-ID'] = clientRequestId;
  }
  if (idempotencyKey) {
    finalHeaders['X-Idempotency-Key'] = idempotencyKey;
  }
  if (authenticated) {
    if (!tokenProvider) {
      throw networkError('Session token provider is not configured.');
    }
    const token = await tokenProvider();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    const url = buildUrl(path);
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: finalHeaders,
        signal,
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (syncErr) {
      if (
        signal &&
        !signal.aborted &&
        syncErr instanceof TypeError &&
        /AbortSignal/i.test(syncErr.message)
      ) {
        response = await fetch(url, {
          method: 'GET',
          headers: finalHeaders,
          credentials: 'omit',
          mode: 'cors',
        });
      } else if (signal?.aborted) {
        throw abortedError();
      } else {
        throw syncErr;
      }
    }
  } catch (err) {
    if (isApiError(err)) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw abortedError();
    }
    throw networkError();
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const requestId = response.headers.get('x-request-id') ?? clientRequestId;
  const filename = safeFilenameFromContentDisposition(response.headers.get('content-disposition'));
  const blob = await response.blob();
  return { blob, response, requestId, filename };
}

/**
 * Parse a filename from a Content-Disposition header, defensively. Returns
 * undefined when no `filename=` is present. The filename is sanitized by the
 * caller (download helper); this only extracts the raw token.
 */
function safeFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  // Prefer filename="..." (quoted); fall back to the unquoted form.
  const quoted = /filename="?([^";]+)"?/i.exec(header);
  if (quoted && quoted[1]) return quoted[1];
  return undefined;
}
